/**
 * A/B Testing Framework
 * レシピ機能の最適化のためのA/Bテストユーティリティ
 */

/**
 * A/Bテスト設定
 */
export const AB_TESTS = {
  RECIPE_NAMING: {
    id: 'recipe_naming_test',
    name: 'レシピコンテンツ名テスト',
    variants: {
      A: {
        id: 'current',
        name: '今ある食材でレシピ',
        weight: 100  // 100% - current implementation
      },
      B: {
        id: 'photo_first',
        name: '冷蔵庫パシャ',
        weight: 0  // Not active yet - for future testing
      }
    },
    metrics: [
      'recipe_hub_view',
      'recipe_feature_click',
      'photo_capture_start',
      'photo_capture_complete',
      'recipe_generation_start',
      'recipe_generation_complete',
      'plan_upgrade_view',
      'plan_upgrade_complete'
    ],
    startDate: new Date('2025-10-26'),
    endDate: new Date('2025-11-09'),  // 2 weeks
    minSampleSize: 100  // per variant
  }
};

/**
 * ユーザーをA/Bテストバリアントに割り当て
 * @param {string} testId - テストID
 * @param {string} userId - ユーザーID
 * @returns {string} - バリアントID ('A' or 'B')
 */
export const getVariant = (testId, userId) => {
  const test = AB_TESTS[testId];
  if (!test) {
    console.warn(`A/B Test not found: ${testId}`);
    return 'A';  // Default to variant A
  }

  // Check if test is active
  const now = new Date();
  if (now < test.startDate || now > test.endDate) {
    return 'A';  // Default to variant A if test is not active
  }

  // Check localStorage for existing assignment
  const storageKey = `ab_test_${testId}_${userId}`;
  const stored = localStorage.getItem(storageKey);
  if (stored && (stored === 'A' || stored === 'B')) {
    return stored;
  }

  // Assign new variant based on weights
  const variants = Object.values(test.variants);
  const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);

  if (totalWeight === 0) {
    return 'A';  // Default if no weights configured
  }

  // Use userId hash for consistent assignment
  const hash = userId.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);

  const rand = Math.abs(hash) % totalWeight;
  let cumulative = 0;

  for (const variant of variants) {
    cumulative += variant.weight;
    if (rand < cumulative) {
      const variantId = variant.id === 'current' ? 'A' : 'B';
      localStorage.setItem(storageKey, variantId);
      return variantId;
    }
  }

  return 'A';  // Fallback
};

/**
 * A/Bテストイベントをトラッキング
 * @param {string} testId - テストID
 * @param {string} userId - ユーザーID
 * @param {string} eventName - イベント名
 * @param {object} metadata - 追加メタデータ
 */
export const trackABTestEvent = async (testId, userId, eventName, metadata = {}) => {
  const test = AB_TESTS[testId];
  if (!test || !test.metrics.includes(eventName)) {
    return;  // Skip if test or metric is not configured
  }

  const variant = getVariant(testId, userId);

  const eventData = {
    testId,
    variant,
    userId,
    eventName,
    timestamp: new Date().toISOString(),
    metadata
  };

  // Send to backend
  try {
    // Store locally for now - will implement backend API
    const storageKey = `ab_events_${testId}`;
    const events = JSON.parse(localStorage.getItem(storageKey) || '[]');
    events.push(eventData);
    localStorage.setItem(storageKey, JSON.stringify(events));

    // TODO: Send to backend API when ready
    // await api.post('/api/ab-test/event', eventData);

    console.log('📊 A/B Test Event:', eventData);
  } catch (error) {
    console.error('Failed to track A/B test event:', error);
  }
};

/**
 * A/Bテスト結果を取得（管理者用）
 * @param {string} testId - テストID
 * @returns {object} - テスト結果
 */
export const getABTestResults = (testId) => {
  const storageKey = `ab_events_${testId}`;
  const events = JSON.parse(localStorage.getItem(storageKey) || '[]');

  const results = {
    testId,
    totalEvents: events.length,
    byVariant: {},
    byMetric: {}
  };

  // Group by variant
  events.forEach(event => {
    if (!results.byVariant[event.variant]) {
      results.byVariant[event.variant] = {
        totalEvents: 0,
        uniqueUsers: new Set(),
        metrics: {}
      };
    }

    results.byVariant[event.variant].totalEvents++;
    results.byVariant[event.variant].uniqueUsers.add(event.userId);

    if (!results.byVariant[event.variant].metrics[event.eventName]) {
      results.byVariant[event.variant].metrics[event.eventName] = 0;
    }
    results.byVariant[event.variant].metrics[event.eventName]++;
  });

  // Convert Sets to counts
  Object.keys(results.byVariant).forEach(variant => {
    results.byVariant[variant].uniqueUsers = results.byVariant[variant].uniqueUsers.size;
  });

  return results;
};

/**
 * A/Bテストデータをエクスポート（CSV形式）
 * @param {string} testId - テストID
 * @returns {string} - CSV文字列
 */
export const exportABTestData = (testId) => {
  const storageKey = `ab_events_${testId}`;
  const events = JSON.parse(localStorage.getItem(storageKey) || '[]');

  if (events.length === 0) {
    return 'No data available';
  }

  // CSV header
  let csv = 'Timestamp,Test ID,Variant,User ID,Event Name,Metadata\n';

  // CSV rows
  events.forEach(event => {
    const metadata = JSON.stringify(event.metadata).replace(/"/g, '""');
    csv += `${event.timestamp},${event.testId},${event.variant},${event.userId},${event.eventName},"${metadata}"\n`;
  });

  return csv;
};
